import { flatMap, flow, initial, last } from 'lodash';

import { TextAlignment } from '../../../../table/CellTypeConstants';
import { TiptapContentNode, TiptapNodeType } from '../../../tiptapTypes';
import { promoteMarksToNodes } from './promoteMarksToNodes';
import { recursivelyStripEmptyMarks } from './recursivelyStripEmptyMarks';
import { LIST_CONTAINER_TYPES } from '../../../extensions/list/tiptapListUtils';

/**
 * Inserts an object in between each item in an array.
 */
const insertBetween = (array: any[], objectToInsert: unknown) =>
    flatMap(initial(array), (item) => [item, objectToInsert]).concat(last(array));

/**
 * Tiptap represents soft-new-lines as "hardBreak" nodes, which are not present in DraftJS.
 * Thus, we need to traverse through the converted Tiptap content, looking for soft-new-lines
 * and replacing them with hardBreak nodes.
 */
const addHardBreaks = (node: TiptapContentNode): TiptapContentNode => {
    if (node.type !== TiptapNodeType.paragraph) return node;

    return {
        ...node,
        content: node.content?.flatMap((child) => {
            if (!child.text || child.type !== TiptapNodeType.text) return child;

            // Split the text node into multiple text nodes, if there's new line characters
            // and add the hardBreak node in between them.
            return insertBetween(
                child.text.split('\n').map((text) => ({ ...child, text })),
                { type: TiptapNodeType.hardBreak },
                // Remove any empty blocks of text
            ).filter((x) => x.text !== '');
        }),
    };
};

const addLeftAlignToParagraphs = (node: TiptapContentNode): TiptapContentNode => {
    if (node.type !== TiptapNodeType.paragraph) return node;
    return {
        ...node,

        attrs: {
            textAlign: TextAlignment.LEFT,
            ...node.attrs,
        },
    };
};

const addStartToOrderedLists = (node: TiptapContentNode): TiptapContentNode => {
    if (node.type !== TiptapNodeType.orderedList) return node;
    return {
        ...node,

        attrs: {
            start: 1,
            ...node.attrs,
        },
    };
};

/**
 * If a list is nested within a parent list item, don't return the parent list & instead
 * recurse the children to find the valid list items.
 */
const getValidListItemContent = (node?: TiptapContentNode): TiptapContentNode[] => {
    if (!node) return [];

    const content = node.content || [];

    if (!content.length) return [node];

    const firstEntry = content[0];

    // The first entry in a list item should always be a paragraph
    if (firstEntry.type === TiptapNodeType.paragraph) return [node];

    // Otherwise it will be a parent list with another list immediately inside it
    //  - this occurs when a Draft.js list is nested without a parent list
    // This isn't a valid structure in Tiptap, so we need to recurse through the children
    // and grab any valid list items.
    return firstEntry.content?.flatMap(getValidListItemContent) || [];
};

/**
 * If a Draft.js list is deeply nested, but it does not have a containing parent list item,
 * the conversion library will create an empty parent item and then a nested child item.
 *
 * This is not a valid content structure in Tiptap, each list item must start with a paragraph.
 *
 * So this function will recurse through the lists and strip out any empty parent list items.
 */
const stripEmptyParentLists = (node: TiptapContentNode): TiptapContentNode => {
    if (!LIST_CONTAINER_TYPES.has(node.type as TiptapNodeType)) return node;

    if (!node.content?.length) return node;

    return {
        ...node,
        content: node.content?.flatMap(getValidListItemContent),
    };
};

export const postprocessContent = flow(
    promoteMarksToNodes,
    recursivelyStripEmptyMarks,
    addLeftAlignToParagraphs,
    addStartToOrderedLists,
    addHardBreaks,
    stripEmptyParentLists,
);
